import React from 'react'
import {
  Badge,
  Button,
  Group,
  MantineTheme,
  NumberInput,
  Paper,
  Select,
  SelectItem,
  Stack,
  Switch,
  Text,
} from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import CustomDatePicker from 'app/views/components/date-picker/date-picker-logic'
import {
  currencyOptions,
  dayBasisOptions,
  roundingOptions,
} from 'app/models/dropdown-options'
import { FacilityParams } from 'app/models/facility-params'
import { toNumberInputValue } from 'app/utils/util-functions'
import { Option } from 'app/models/dropdown-options'
import { getPikConfig } from '../loans/utilities/pik-config-utils'
import WarningText from 'app/views/components/warning-text/warning-text'
import { deriveAllInRate, rateDiffers } from 'app/utils/rate-rounding'

type Props = {
  form: any
  index: number
  facility: FacilityParams | undefined
  facilityindexOptions: Option[]
  facilityContractPeriodOptions: SelectItem[]
  isContractPeriodDisabled: boolean
  pikConfig: ReturnType<typeof getPikConfig>
  onRemove: () => void
}

export default function SplitLoanPanel({
  form,
  index,
  facility,
  facilityindexOptions,
  facilityContractPeriodOptions,
  isContractPeriodDisabled,
  pikConfig,
  onRemove,
}: Props) {
  const prefix = `newLoans.${index}`
  const panel = form.values.newLoans?.[index] ?? {}
  // The ledger reads fxRate off each new loan of a split, not off the split
  // itself, so every panel in a non-base currency needs its own rate.
  const isForeignCurrencyPanel =
    !!facility?.baseCurrency &&
    !!panel.currency &&
    panel.currency !== facility.baseCurrency
  const panelPikEnabled = panel.newLoanWithPik === true

  // Auto-calculate the rounded base rate and All-In Rate for this panel:
  // All-In = ROUNDED base rate + margin + CAS + PIK premium. Shown value
  // includes PIK Premium; createDraftRolloverSplit splits it back out into
  // ledger `allInRate` (without premium) and `allInRateWithPikPremium` at
  // save time.
  //
  // The derived fields are dependencies too, guarded by `rateDiffers`, so the
  // effect also corrects them when something ELSE writes them: editing an
  // existing draft re-hydrates the panels from the saved split in a parent
  // effect, which runs after this one and used to leave a stale all-in rate
  // in place because none of the inputs had changed.
  const derivedRates = deriveAllInRate({
    interestBaseRate: panel.interestBaseRate,
    rounding: panel.rounding,
    margin: panel.margin,
    casValue: panel.casValue,
    pikPremium: panel.pikPremium,
  })
  React.useEffect(() => {
    const { roundedBaseRate, allInRate } = derivedRates
    if (rateDiffers(panel.interestBaseRateWithRounding, roundedBaseRate)) {
      form.setFieldValue(`${prefix}.interestBaseRateWithRounding`, roundedBaseRate)
    }
    if (rateDiffers(panel.allInRate, allInRate)) {
      form.setFieldValue(`${prefix}.allInRate`, allInRate)
    }
    if (rateDiffers(panel.allInRateWithPikPremium, allInRate)) {
      form.setFieldValue(`${prefix}.allInRateWithPikPremium`, allInRate)
    }
  }, [
    derivedRates.roundedBaseRate,
    derivedRates.allInRate,
    panel.interestBaseRateWithRounding,
    panel.allInRate,
    panel.allInRateWithPikPremium,
  ])

  // When the user types into the Interest Rate field, only the base rate is
  // written here; the rounded rate and allInRate follow via the effect above.
  const onInterestBaseRateChange = (value: number | '') => {
    form.setFieldValue(`${prefix}.interestBaseRate`, value)
  }

  // Mirror the non-split `changeContractPeriod`: look up the CAS for the
  // panel's index option + newly-selected contract period and write it to the
  // panel. allInRate recomputes via the useEffect above.
  const onContractPeriodChange = (value: string | null) => {
    form.setFieldValue(`${prefix}.contractPeriod`, value)
    if (!value || !facility) {
      return
    }
    const panelIndexId =
      typeof panel.indexOption === 'string'
        ? (panel.indexOption as string)
        : (panel.indexOption?.id ?? '')
    const matchingIro = facility.iroValues?.find(
      iro => iro.indexOption.id === panelIndexId,
    )
    const matchingContractPeriod = matchingIro?.contractPeriod?.find(
      cp => cp.contractPeriod === value,
    )
    if (!matchingContractPeriod) {
      return
    }
    form.setFieldValue(
      `${prefix}.casValue`,
      Number(matchingContractPeriod.cas) || 0,
    )
  }

  // When the panel's Index Rate Option changes, refill margin / dayBasis /
  // rounding from the facility's matching iroValues entry, and reset
  // contractPeriod, casValue, and interestBaseRate fields since they were
  // tied to the previous index. The panel's auto-recalc useEffect above
  // re-derives allInRate from the new margin once these are set.
  const onIndexOptionChange = (value: string | null) => {
    form.setFieldValue(`${prefix}.indexOption`, value)
    form.setFieldValue(`${prefix}.contractPeriod`, '')
    form.setFieldValue(`${prefix}.casValue`, 0)
    form.setFieldValue(`${prefix}.interestBaseRate`, 0)
    form.setFieldValue(`${prefix}.interestBaseRateWithRounding`, 0)
    if (!value || !facility) {
      form.setFieldValue(`${prefix}.margin`, 0)
      form.setFieldValue(`${prefix}.dayBasis`, null)
      form.setFieldValue(`${prefix}.rounding`, null)
      return
    }
    const matchingIro = facility.iroValues?.find(
      iro =>
        iro.indexOption.id === value && iro.currency === panel.currency,
    )
    if (!matchingIro) {
      form.setFieldValue(`${prefix}.margin`, 0)
      form.setFieldValue(`${prefix}.dayBasis`, null)
      form.setFieldValue(`${prefix}.rounding`, null)
      return
    }
    form.setFieldValue(`${prefix}.margin`, Number(matchingIro.margin) || 0)
    form.setFieldValue(`${prefix}.dayBasis`, matchingIro.dayBasis)
    form.setFieldValue(`${prefix}.rounding`, matchingIro.rounding)
  }

  return (
    <Paper withBorder radius={10} p="md">
      <Stack
        spacing={0}
        sx={{
          '& > *:not(:first-of-type)': { marginTop: 6 },
        }}
      >
        <Group position="apart">
          <Text weight={600}>Loan {index + 1}</Text>
          <Badge
            variant="light"
            color="blue"
            radius={5}
            styles={(theme: MantineTheme) => ({
              root: {
                textTransform: 'none',
                paddingTop: 2,
                paddingBottom: 2,
                paddingLeft: 10,
                paddingRight: 10,
                height: 'auto',
                color: theme.colors.gray[7],
                fontWeight: 500,
                fontSize: 12,
              },
            })}
          >
            new
          </Badge>
          {index > 0 && (
            <Button
              variant="subtle"
              color="gray"
              compact
              leftIcon={<IconX size={16} />}
              onClick={onRemove}
              aria-label={`Remove Loan ${index + 1}`}
              styles={{
                label: { fontSize: 14, fontWeight: 400 },
                leftIcon: { marginRight: 2 },
              }}
            >
              remove
            </Button>
          )}
        </Group>

        <Group noWrap grow>
          <Select
            searchable
            withAsterisk
            label="Index Rate"
            placeholder="Select Index Rate"
            data={facilityindexOptions}
            {...form.getInputProps(`${prefix}.indexOption`)}
            onChange={onIndexOptionChange}
          />
          <Select
            searchable
            withAsterisk
            readOnly
            data={currencyOptions}
            label="Currency"
            {...form.getInputProps(`${prefix}.currency`)}
          />
          {isForeignCurrencyPanel && (
            <NumberInput
              hideControls
              precision={6}
              label="FX Rate"
              placeholder="Enter FX rate"
              description={`${panel.currency} → ${facility?.baseCurrency}`}
              min={0}
              {...form.getInputProps(`${prefix}.fxRate`)}
              value={toNumberInputValue(panel.fxRate)}
            />
          )}
          <NumberInput
            hideControls
            withAsterisk
            precision={2}
            label="Amount"
            placeholder="Enter Amount"
            parser={value =>
              value ? value.replace(/\$\s?|(,*)/g, '') : ''
            }
            formatter={value =>
              !Number.isNaN(parseFloat(value ?? ''))
                ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                : ''
            }
            {...form.getInputProps(`${prefix}.amount`)}
          />
        </Group>

        <Select
          searchable
          disabled={isContractPeriodDisabled}
          label="Contract Period"
          placeholder="Select Contract Period"
          data={facilityContractPeriodOptions}
          {...form.getInputProps(`${prefix}.contractPeriod`)}
          onChange={onContractPeriodChange}
        />

        <Group noWrap grow>
          <CustomDatePicker
            required
            label="Start Date"
            date={panel.startDate}
            setDate={(value: any) =>
              form.setFieldValue(`${prefix}.startDate`, value)
            }
            holidayCalendars={facility?.holidayCalendar ?? []}
          />
          <CustomDatePicker
            required
            label="Maturity Date"
            date={panel.endDate}
            setDate={(value: any) =>
              form.setFieldValue(`${prefix}.endDate`, value)
            }
            holidayCalendars={facility?.holidayCalendar ?? []}
          />
          <Select
            searchable
            readOnly
            data={dayBasisOptions}
            label="Day Basis"
            {...form.getInputProps(`${prefix}.dayBasis`)}
          />
        </Group>

        <Group noWrap grow>
          <NumberInput
            hideControls
            precision={5}
            label="Interest Rate"
            parser={value =>
              value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
            }
            formatter={value =>
              !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
            }
            {...form.getInputProps(`${prefix}.interestBaseRate`)}
            onChange={onInterestBaseRateChange}
          />
          <NumberInput
            hideControls
            precision={5}
            label="Interest Rate with Rounding"
            parser={value =>
              value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
            }
            formatter={value =>
              !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
            }
            {...form.getInputProps(`${prefix}.interestBaseRateWithRounding`)}
          />
          <Select
            searchable
            data={roundingOptions}
            readOnly
            label="Rounding"
            {...form.getInputProps(`${prefix}.rounding`)}
          />
        </Group>

        <Group noWrap grow>
          <NumberInput
            hideControls
            precision={5}
            label="Margin"
            parser={value =>
              value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
            }
            formatter={value =>
              !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
            }
            {...form.getInputProps(`${prefix}.margin`)}
          />
          {panel.casValue !== undefined && panel.casValue !== 0 && (
            <NumberInput
              hideControls
              readOnly
              precision={5}
              label="CAS Rate"
              parser={value =>
                value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
              }
              formatter={value =>
                !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
              }
              {...form.getInputProps(`${prefix}.casValue`)}
            />
          )}
          <NumberInput
            hideControls
            readOnly
            className="applyGrayBackground"
            precision={5}
            label="All In Rate"
            parser={value =>
              value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
            }
            formatter={value =>
              !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
            }
            {...form.getInputProps(`${prefix}.allInRate`)}
          />
        </Group>

        {pikConfig.showNewLoanPikToggle && (
          <Group>
            <Switch
              labelPosition="left"
              label="New Loan PIK (ON/OFF)"
              size="lg"
              checked={panelPikEnabled}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const checked = event.currentTarget.checked
                form.setFieldValue(`${prefix}.newLoanWithPik`, checked)
                if (!checked) {
                  form.setFieldValue(`${prefix}.pikPremium`, 0)
                  form.setFieldValue(`${prefix}.pikInterestPercent`, 0)
                  form.setFieldValue(`${prefix}.cashInterestPercent`, 0)
                }
              }}
              classNames={{ track: 'primary-switch-track' }}
            />
          </Group>
        )}

        {(pikConfig.showNewLoanPikFields ||
          (pikConfig.showNewLoanPikFieldsWhenEnabled && panelPikEnabled)) && (
          <Group noWrap grow>
            <NumberInput
              hideControls
              precision={5}
              label="PIK Premium (%)"
              parser={value =>
                value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
              }
              formatter={value =>
                !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
              }
              {...form.getInputProps(`${prefix}.pikPremium`)}
            />
            <NumberInput
              hideControls
              withAsterisk
              precision={5}
              label="PIK Interest (%)"
              parser={value =>
                value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
              }
              formatter={value =>
                !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
              }
              {...form.getInputProps(`${prefix}.pikInterestPercent`)}
            />
            <NumberInput
              hideControls
              withAsterisk
              precision={5}
              label="Cash Interest (%)"
              parser={value =>
                value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
              }
              formatter={value =>
                !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
              }
              {...form.getInputProps(`${prefix}.cashInterestPercent`)}
            />
          </Group>
        )}
        {(pikConfig.showNewLoanPikFields ||
          (pikConfig.showNewLoanPikFieldsWhenEnabled && panelPikEnabled)) &&
          (() => {
            const pikInterest = Number(panel.pikInterestPercent) || 0
            const cashInterest = Number(panel.cashInterestPercent) || 0
            const allInRateWithPik =
              Number(panel.allInRateWithPikPremium) ||
              Number(panel.allInRate) ||
              0
            return (
              Math.abs(pikInterest + cashInterest - allInRateWithPik) >= 0.01
            )
          })() && (
            <WarningText
              type="error"
              text="PIK Cash % + PIK Interest % must equal All In Rate"
            />
          )}
      </Stack>
    </Paper>
  )
}
